import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Invoice : famille "document de facturation".
 *
 * Pièce de facturation à lignes et taxes, émise ou reçue (facture, acompte
 * facturé, avoir), dont on suit l'échéance et le règlement. AUCUNE écriture
 * comptable ici : le socle ne fait que décrire et suivre la pièce.
 *
 * Modèle "famille + type" (cf. patron asset) : le socle ne connaît pas la
 * nature métier concrète. Le discriminant `type` porte le genre de pièce
 * déclaré par l'app ("facture", "acompte", "avoir") ; `direction` (émise,
 * reçue) et `status` (brouillon, envoyée, payée, en retard) sont des états
 * métier à part, chaînes libres pilotées par l'app.
 *
 * Les liens vers les autres familles (contact, order, payment, subscription,
 * project) sont de simples ids string, jamais un import d'un autre schéma.
 * `contactId` (la partie facturée ou facturante) est le seul lien requis :
 * une facture vise toujours quelqu'un.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). L'accès fin par rôle
 * (vente, caisse, facturation) et le portail client (chaque contact ne voit
 * que ses propres pièces) viendront après v0, hors de ce schéma.
 */

/** Champs métier de la famille invoice (hors champs standard du socle). */
export const invoiceCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "facture", "acompte", "avoir"). */
  type: z.string().min(1),
  /** Contact visé par la pièce (client si émise, fournisseur si reçue). Requis. */
  contactId: z.string().min(1),
  /** Sens de la pièce, chaîne libre pilotée par l'app (ex. "emise", "recue"). Optionnel. */
  direction: z.string().min(1).optional(),
  /** Numéro de pièce (souvent absent tant que la facture est en brouillon). Optionnel. */
  number: z.string().min(1).optional(),
  /** Lignes de la pièce (désignation, quantité, prix, taxe...). Liste structurée, optionnel. */
  lines: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Date d'échéance du règlement. Optionnel. */
  dueAt: z.date().optional(),
  /** État courant, chaîne libre pilotée par l'app (ex. "brouillon", "envoyee", "payee", "en_retard"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Montant total TTC (taxes comprises). Optionnel. */
  totalInclTax: z.number().optional(),
  /** Commande d'origine si la pièce en découle. Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Règlement rattaché à la pièce. Optionnel. */
  paymentId: z.string().min(1).optional(),
  /** Abonnement d'origine si la pièce est une échéance récurrente. Optionnel. */
  subscriptionId: z.string().min(1).optional(),
  /** Projet de rattachement. Optionnel. */
  projectId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille invoice (champs standard + champs métier). */
export const InvoiceSchema = z.object({
  ...baseEntityFields,
  ...invoiceCoreFields,
});

/** Type d'une invoice. */
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const InvoiceCreateInputSchema = z.object({
  ...invoiceCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une invoice. */
export type InvoiceCreateInput = z.infer<typeof InvoiceCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const InvoiceUpdateInputSchema = z
  .object({ ...invoiceCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'une invoice. */
export type InvoiceUpdateInput = z.infer<typeof InvoiceUpdateInputSchema>;
